import type { ChatMessage, ChatUser, ReactionKey } from './chat.types';
import type { HTMLAttributes } from 'react';

import { classNames, usePrevious } from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Message } from './message';

import chatStyles from './chat.module.css';
import styles from './messages-list.module.css';

type Props = {
  messages: ChatMessage[];
  userId?: ChatUser['userId'];
  userIsModerator?: boolean;
  loading?: boolean;
  messageTestId?: string;
  readOnly?: boolean;
  anonymous?: boolean;
  onEditMessage?: (message: ChatMessage) => void;
  onDeleteMessage?: (message: ChatMessage) => void;
  onReactToMessage?: (messageId: number, reactionName: ReactionKey) => void;
  onReplyToMessage?: (messageId: ChatMessage['messageId']) => void;
} & HTMLAttributes<HTMLDivElement>;

const REACTIONS_BAR_HEIGHT = 30;

export function MessagesList({
  messages,
  userId,
  userIsModerator,
  loading,
  messageTestId,
  readOnly,
  anonymous,
  onEditMessage,
  onDeleteMessage,
  onReactToMessage,
  onReplyToMessage,
  ...props
}: Props) {
  const { t } = useTranslation();
  const previousMessagesLength = usePrevious(messages.length);
  const messagesListRef = useRef<HTMLDivElement>(null);
  const [newMessagesArrived, setNewMessagesArrived] = useState(false);
  const [messagesListIsAtBottom, setMessagesListIsAtBottom] = useState(true);

  const scrollMessagesListToBottom = () => {
    setTimeout(() => messagesListRef.current?.scrollTo({ top: messagesListRef.current.scrollHeight }), 0);
  };

  const handleNewMessagesIndicatorClick = () => {
    scrollMessagesListToBottom();
    setNewMessagesArrived(false);
  };

  const handleScroll = () => {
    const messagesList = messagesListRef.current;

    if (!messagesList) return;

    const scrollbarAtBottom =
      messagesList.scrollHeight - messagesList.clientHeight - messagesList.scrollTop <= REACTIONS_BAR_HEIGHT;

    setMessagesListIsAtBottom(scrollbarAtBottom);
    if (scrollbarAtBottom) setNewMessagesArrived(false);
  };

  useEffect(scrollMessagesListToBottom, []);

  useEffect(() => {
    const messagesList = messagesListRef.current;

    if (!messagesList) return;

    const numberOfMessages = messages.length;
    const newMessagesArrived = numberOfMessages > previousMessagesLength;

    if (!newMessagesArrived) return;

    const lastMessage = messages[numberOfMessages - 1];
    const lastMessageSentByUser = lastMessage.sender?.userId === userId;
    const allMessagesAreVisible = messagesList.scrollHeight <= messagesList.clientHeight;

    if (!allMessagesAreVisible && !lastMessageSentByUser) setNewMessagesArrived(true);
    if (messagesListIsAtBottom || lastMessageSentByUser) scrollMessagesListToBottom();
  }, [userId, messages, messagesListIsAtBottom, previousMessagesLength]);

  return (
    <div {...props} className={classNames(styles.messagesList, props.className)}>
      {newMessagesArrived && (
        <span
          className={classNames(chatStyles.popup, styles.newMessagePopup)}
          onClick={handleNewMessagesIndicatorClick}
        >
          {t('common.chat.newMessagesIndicator')}
        </span>
      )}

      <div
        data-testid="chat-messages-list"
        ref={messagesListRef}
        className={classNames(styles.messages, 'lspro-scrollbars')}
        onScroll={handleScroll}
      >
        {messages.map((message) => (
          <Message
            key={message.messageId}
            testId={messageTestId}
            message={message}
            currentUserId={userId}
            userIsModerator={userIsModerator}
            loading={loading}
            readOnly={readOnly}
            anonymous={anonymous}
            onDeleteMessage={onDeleteMessage}
            onEditMessage={onEditMessage}
            onReactToMessage={onReactToMessage}
            onReplyToMessage={onReplyToMessage}
          />
        ))}
      </div>
    </div>
  );
}
